import moment from 'moment';
import { v4 as uuid } from 'uuid';
import { CaseVoucherSource, isAssociationManaged, } from '../../case-vouchers/index.js';
import { FixedDistributionAccount, FixedDistributionDateBehavior, appliesTo, } from '../../fixed-distributions/index.js';
import { VoucherTypeIds } from '../../vouchers/index.js';
import { matchesVoucherType, } from '../../vouchers/matcher.js';
import { dayIn, isoDate } from '../engine-v2/dates.js';
import { amountsOverTime } from '../engine-v2/index.js';
import helpers from '../index.js';
import { buildReferenceVoucherEarmarks, } from '../interest/index.js';
import { isEngineV2 } from '../policy.js';
import { addDatesToCaseVoucher, sortBy, sortBySameCategoryDateDescFirst, sortByWithExtraDiscriminator, } from '../utils.js';
const tagIsActive = (tag, date) => {
    const from = tag.from != null ? moment(date).isAfter(tag.from) : true;
    const to = tag.to != null ? moment(date).isBefore(tag.to) : true;
    return from && to;
};
export const getCollectionCommissionMatches = (billingModel, _case, date, caseVoucherWithDistribution) => {
    return billingModel.model.share.filter((share) => {
        // Case tag
        const caseIsTagged = !share.caseTag ||
            (share.caseTag &&
                _case.tags.some((tag) => tag.tag === share.caseTag && tagIsActive(tag, date)));
        // Case is company
        // TODO: From customer?
        const caseIsCompany = typeof share.caseIsCompany !== 'boolean' ||
            share.caseIsCompany === _case.isCompany;
        // Case voucher tag
        const caseVoucherIsTagged = !share.caseVoucherTag ||
            (share.caseVoucherTag &&
                caseVoucherWithDistribution &&
                caseVoucherWithDistribution.tags.some((tag) => tag.tag === share.caseVoucherTag && tagIsActive(tag, date)));
        // Category
        const caseVoucherHasCategory = !share.categories ||
            (share.categories &&
                caseVoucherWithDistribution &&
                share.categories.includes(caseVoucherWithDistribution.category));
        // Case voucher type
        const caseVoucherIsOfVoucherType = !share.voucherTypeIds ||
            (share.voucherTypeIds &&
                caseVoucherWithDistribution &&
                share.voucherTypeIds.includes(caseVoucherWithDistribution.voucherTypeId));
        // Case voucher source
        const caseVoucherSource = !share.caseVoucherSource ||
            share.caseVoucherSource === caseVoucherWithDistribution.source;
        return (caseIsTagged &&
            caseIsCompany &&
            caseVoucherIsTagged &&
            caseVoucherHasCategory &&
            caseVoucherIsOfVoucherType &&
            caseVoucherSource);
    });
};
const addInterest = (documents, economyPolicy, fixedDistributions, conditions) => async (currState, prevState, voucher, nextVoucher) => {
    const until = moment(nextVoucher ? nextVoucher.date : voucher.date).format();
    const vouchersWithInterest = await helpers.interest.calculate(currState, documents, economyPolicy, fixedDistributions, until, conditions);
    // Add interest to date
    return currState.map((voucher) => {
        const voucherWithInterest = vouchersWithInterest.find((v) => v.caseVoucherId === voucher.caseVoucherId);
        voucher.amount = voucherWithInterest
            ? voucherWithInterest.amount
            : voucher.amount;
        return voucher;
    });
};
// Billing allocates by the creditor's distributions, while the amounts it
// allocates come from the economy, which follows the debtor's.
const creditorDistributions = (fixedDistributions) => fixedDistributions.filter(appliesTo(FixedDistributionAccount.Creditor));
const distributeWithInterest = (caseVouchers, documents, economyPolicy, billingModel, fixedDistributions, conditions) => {
    const modelPriorities = billingModel.model;
    let filters = [sortBySameCategoryDateDescFirst];
    if (modelPriorities.voucherCategoryPriority) {
        const categoryPriority = modelPriorities.voucherCategoryPriority;
        filters = filters.concat((a, b) => (categoryPriority.findIndex((p) => p === a.category) || 0) -
            (categoryPriority.findIndex((p) => p === b.category) || 0));
    }
    if (modelPriorities.voucherTypePriority) {
        const typePriority = modelPriorities.voucherTypePriority;
        filters = filters.concat((a, b) => (typePriority.findIndex((p) => p === a.voucherTypeId) || 0) -
            (typePriority.findIndex((p) => p === b.voucherTypeId) || 0));
    }
    if (modelPriorities.voucherTagPriority) {
        const tagPriority = modelPriorities.voucherTagPriority;
        filters = filters.concat((a, b) => {
            const aTag = a.tags.filter((tag) => tagPriority.includes(tag.tag))[0] || null;
            const bTag = b.tags.filter((tag) => tagPriority.includes(tag.tag))[0] || null;
            return (((aTag &&
                aTag.tag &&
                tagPriority.findIndex((tag) => tag === aTag.tag)) ||
                0) -
                ((bTag &&
                    bTag.tag &&
                    tagPriority.findIndex((tag) => tag === bTag.tag)) ||
                    0));
        });
    }
    const dateFilter = (a, b) => modelPriorities.voucherDatePriorityNewFirst
        ? b.rawDate - a.rawDate
        : a.rawDate - b.rawDate;
    filters = filters.concat([dateFilter, (a, b) => a.id - b.id]);
    if (isEngineV2(economyPolicy)) {
        return distributeWithEngineV2(caseVouchers, documents, economyPolicy, billingModel, fixedDistributions, conditions, sortByWithExtraDiscriminator(...filters));
    }
    return helpers.distributor.distribute(caseVouchers.map(addDatesToCaseVoucher), sortByWithExtraDiscriminator(...filters), creditorDistributions(fixedDistributions), {
        buildDistributionEarmarks: buildReferenceVoucherEarmarks,
        modifier: addInterest(documents, economyPolicy, fixedDistributions, conditions),
        oldFilterByAmountIfDateIsEqual: modelPriorities.oldFilterByAmountIfDateIsEqual,
    });
};
// v1 recalculates the whole economy at every billing step. Engine v2 replays
// once, recording each voucher's amount at the instants billing steps end, and
// pins fixed distributions to what the economy actually applied.
const distributeWithEngineV2 = async (caseVouchers, documents, economyPolicy, billingModel, fixedDistributions, conditions, order) => {
    // Distribute appends these very objects, keeping each one's distribution
    // on it as it goes.
    const vouchers = caseVouchers.map((voucher) => ({
        ...addDatesToCaseVoucher(voucher),
        distribution: [],
        distributionAmount: 0,
    }));
    // Billing steps end on the next voucher's day.
    const day = dayIn(economyPolicy.timezone);
    const stepEnd = (voucher) => day(voucher.date);
    const times = [...new Set(vouchers.map(stepEnd))].toSorted((a, b) => a - b);
    const until = times[times.length - 1];
    const economy = await amountsOverTime(vouchers, documents, economyPolicy, fixedDistributions, isoDate(until ?? 0).slice(0, 10), times, conditions);
    const credits = economy.caseVouchers.filter((voucher) => voucher.amount > 0);
    const billingDistributions = pinDistributions(creditorDistributions(fixedDistributions), economy.caseVouchers, new Map(credits.map((credit) => [
        credit.caseVoucherId,
        Math.min(credit.amount, credit.distributionAmount),
    ])));
    const allowance = creditAllowance(vouchers, credits, stepEnd, order, billingDistributions.filter((fd) => fd.dateBehavior === FixedDistributionDateBehavior.FromApplied));
    const state = await helpers.distributor.distribute(vouchers, order, billingDistributions, {
        buildDistributionEarmarks: (earmarkVouchers, reservations) => allowance.noteEarmarks(buildReferenceVoucherEarmarks(earmarkVouchers, reservations)),
        modifier: (currState, _prevState, voucher, nextVoucher) => {
            const amounts = economy.snapshots.get(stepEnd(nextVoucher ?? voucher));
            if (amounts !== undefined) {
                for (const stateVoucher of currState) {
                    stateVoucher.amount =
                        amounts.get(stateVoucher.caseVoucherId) ?? stateVoucher.amount;
                }
            }
            allowance.allow(currState, nextVoucher);
            return Promise.resolve(currState);
        },
        oldFilterByAmountIfDateIsEqual: billingModel.model.oldFilterByAmountIfDateIsEqual,
    });
    // What of a payment was not billed is reported as an overpayment.
    allowance.restore(state);
    return state;
};
// Billing distributes no more of the payments in all than the economy applied
// of them by the end of each day: what the economy left unapplied, or gave to
// debt it later wrote off, is an overpayment there too. Within that, billing
// keeps its own order: before each voucher is appended, what is left is given
// to the payments in the order that voucher takes them. A payment keeps what
// its fixed distributions and earmarks pin of it.
const creditAllowance = (vouchers, credits, stepEnd, order, deferredDistributions) => {
    const enteredByCredit = new Map(credits.map((credit) => [credit.caseVoucherId, credit.amount]));
    const appliedByDay = credits.flatMap((credit) => credit.distribution
        .filter((entry) => entry.amount > 0)
        .map((entry) => ({
        day: Math.max(stepEnd(entry), stepEnd(credit)),
        amount: entry.amount,
    })));
    const appliedThrough = (day) => appliedByDay
        .filter((applied) => applied.day <= day)
        .reduce((sum, applied) => sum + applied.amount, 0);
    // Applied at the payment's own turn, after the allowance is given.
    const pendingByCredit = new Map();
    const pend = (caseVoucherId, amount) => pendingByCredit.set(caseVoucherId, (pendingByCredit.get(caseVoucherId) ?? 0) + amount);
    for (const fd of deferredDistributions) {
        pend(fd.fromCaseVoucherId, fd.amount);
    }
    const isCredit = (voucher) => enteredByCredit.has(voucher.caseVoucherId);
    // The next voucher to append, as distribute holds it.
    const vouchersById = new Map(vouchers.map((voucher) => [voucher.caseVoucherId, voucher]));
    return {
        noteEarmarks: (earmarks) => {
            for (const earmark of earmarks) {
                pend(earmark.sourceVoucherId, earmark.amount);
            }
            return earmarks;
        },
        allow: (state, next) => {
            const appended = state.filter(isCredit);
            for (const credit of appended) {
                credit.amount = credit.distributionAmount;
            }
            if (next === undefined) {
                return;
            }
            // Distribute applies corrections from the origin before it starts, so
            // what a payment not yet appended corrected counts from its own turn,
            // as the economy's does from its day.
            const nextCredit = isCredit(next)
                ? vouchersById.get(next.caseVoucherId)
                : undefined;
            let left = appliedThrough(stepEnd(next)) -
                [...appended, ...(nextCredit === undefined ? [] : [nextCredit])].reduce((sum, credit) => sum + credit.distributionAmount, 0);
            const takers = nextCredit === undefined
                ? appended.toSorted((a, b) => order(a, b, next))
                : [nextCredit];
            for (const credit of takers) {
                const pending = pendingByCredit.get(credit.caseVoucherId) ?? 0;
                const share = Math.max(pending, Math.min(enteredByCredit.get(credit.caseVoucherId) -
                    credit.distributionAmount, Math.max(0, left)));
                credit.amount = credit.distributionAmount + share;
                left -= share;
                pendingByCredit.delete(credit.caseVoucherId);
            }
        },
        // What a joint mirror did not apply is not money on this case (D9).
        restore: (state) => {
            for (const voucher of state.filter(isCredit)) {
                voucher.amount = isAssociationManaged(voucher)
                    ? voucher.distributionAmount
                    : enteredByCredit.get(voucher.caseVoucherId);
            }
        },
    };
};
// Distributions on both accounts take what the economy applied of them, and
// creditor-only ones what is left of their payment's applied amount.
const pinDistributions = (billingDistributions, economyVouchers, appliedByCredit) => {
    const appliedByDistribution = new Map();
    for (const voucher of economyVouchers) {
        for (const entry of voucher.distribution) {
            if (entry.fixedDistributionId !== undefined && entry.amount < 0) {
                appliedByDistribution.set(entry.fixedDistributionId, (appliedByDistribution.get(entry.fixedDistributionId) ?? 0) -
                    entry.amount);
            }
        }
    }
    const unpinnedByCredit = new Map(appliedByCredit);
    const pinned = new Map();
    const take = (fd, amount) => {
        const unpinned = unpinnedByCredit.get(fd.fromCaseVoucherId);
        const taken = unpinned === undefined ? amount : Math.min(amount, unpinned);
        if (unpinned !== undefined) {
            unpinnedByCredit.set(fd.fromCaseVoucherId, unpinned - taken);
        }
        pinned.set(fd.id, taken);
    };
    for (const fd of billingDistributions) {
        if (fd.account === FixedDistributionAccount.Both) {
            take(fd, appliedByDistribution.get(fd.id) ?? 0);
        }
    }
    for (const fd of billingDistributions) {
        if (fd.account !== FixedDistributionAccount.Both) {
            take(fd, fd.amount);
        }
    }
    return billingDistributions.map((fd) => ({
        ...fd,
        amount: pinned.get(fd.id),
    }));
};
export const getVoucherWithInterest = async (billingModel, fixedDistributions, conditions, caseVouchers, documents, economyPolicy) => {
    if (caseVouchers && documents && economyPolicy) {
        return await distributeWithInterest(helpers.vouchers
            .postProcessing(caseVouchers, {
            includeIrrecoverableVouchers: false,
            includeDeletedVouchers: false,
            includeExpiredVouchers: false,
        })
            .filter((voucher) => billingModel.model.exclude == null ||
            !matchesVoucherType(billingModel.model.exclude, voucher)), documents, economyPolicy, billingModel, fixedDistributions, conditions);
    }
    return null;
};
export const getBaseBillingVoucher = (baseVoucher, voucherWithDistribution, billingModel) => ({
    ...baseVoucher,
    billingModelId: billingModel.billingModelId,
    currency: voucherWithDistribution.contraCaseVoucher.currency,
    date: voucherWithDistribution.contraCaseVoucher.date,
    caseVoucherId: voucherWithDistribution.caseVoucherId,
    depositCaseVoucherId: voucherWithDistribution.contraCaseVoucher.caseVoucherId,
    caseVoucher: {
        caseVoucherId: voucherWithDistribution.caseVoucherId,
        voucherTypeId: voucherWithDistribution.voucherTypeId,
        date: voucherWithDistribution.date,
        id: voucherWithDistribution.id,
        referenceId: voucherWithDistribution.referenceId,
        source: voucherWithDistribution.source,
        amount: voucherWithDistribution.amount,
    },
    depositCaseVoucher: mapDepositCaseVoucher(voucherWithDistribution.contraCaseVoucher),
});
export const caseBillingIsActive = (date, _case) => _case.billingStartDate != null
    ? moment(date).isAfter(_case.billingStartDate)
    : true;
export const findAndSort = (caseVouchers, from) => {
    const vouchersOfType = from
        ? caseVouchers.filter((v) => matchesVoucherType(from, v))
        : caseVouchers;
    const vouchersWithDistribution = vouchersOfType
        .map((caseVoucher) => caseVoucher.distribution.map((caseVoucherWithDistribution) => {
        const v = caseVouchers.find((v) => v.caseVoucherId === caseVoucherWithDistribution.caseVoucherId);
        if (!v) {
            throw new Error('Could not find voucher in distribution');
        }
        return {
            ...v,
            contraCaseVoucher: caseVoucher,
            amountFromVoucher: caseVoucherWithDistribution.amount,
        };
    }), [])
        .reduce((acc, vouchers) => acc.concat(vouchers), []);
    return vouchersWithDistribution.sort(sortBy((a, b) => new Date(a.contraCaseVoucher.date).getTime() -
        new Date(b.contraCaseVoucher.date).getTime(), (a, b) => a.id - b.id));
};
export const isProbablyLastDepositOnAccount = async (caseVouchers, documents, economyPolicy, date, fixedDistributions, conditions) => {
    const vouchers = await helpers.interest.calculate(caseVouchers, documents, economyPolicy, fixedDistributions, date, conditions);
    const isLast = helpers.overview.balance(vouchers) > -4500;
    return isLast;
};
export const genResidueVouchers = (shareAmount, voucherWithDistribution, shareBaseVoucher, billingModel, _case) => {
    const residueAmount = voucherWithDistribution.amountFromVoucher - shareAmount;
    const residueVoucherIsNotZero = residueAmount !== 0;
    const voucherIsNotCreditorTransaction = voucherWithDistribution.contraCaseVoucher.source !==
        CaseVoucherSource.Creditor;
    if (residueVoucherIsNotZero &&
        caseBillingIsActive(voucherWithDistribution.contraCaseVoucher.date, _case) &&
        (voucherIsNotCreditorTransaction ||
            billingModel.model.isAccountsReceivableBillingModel)) {
        const newResidueVoucher = {
            ...shareBaseVoucher,
            billingVoucherId: uuid(),
            billingModelId: billingModel.billingModelId,
            voucherTypeId: VoucherTypeIds.DEPOSITPAYOUT,
            amount: Math.round(residueAmount),
        };
        if (newResidueVoucher.amount) {
            return newResidueVoucher;
        }
    }
    return null;
};
export const getToVoucherTypeIds = (billingModel, voucherWithDistribution, collectionCommissionMatch) => {
    if (billingModel.model.clientDepositToVoucherTypeId &&
        voucherWithDistribution.contraCaseVoucher.source ===
            CaseVoucherSource.Creditor) {
        return [billingModel.model.clientDepositToVoucherTypeId];
    }
    const toVoucherType = collectionCommissionMatch.toVoucherTypeId ||
        collectionCommissionMatch.toVoucherTypeIds ||
        billingModel.model.toVoucherTypeId;
    const toVoucherTypeIds = typeof toVoucherType === 'string' ? [toVoucherType] : toVoucherType;
    return toVoucherTypeIds;
};
export const handleSavedUpVouchers = (newResidueVouchers, savedUpVouchers) => {
    let newVouchers = [];
    for (let x = 0; x < newResidueVouchers.length; x++) {
        const newResidueVoucher = newResidueVouchers[x];
        for (let i = 0; i < savedUpVouchers.length; i++) {
            const savedUpVoucher = savedUpVouchers[i];
            // Prevent crediting newResidueVouchers if it is already billed
            if (moment(newResidueVoucher.date).isBefore(moment(savedUpVoucher.date))) {
                continue;
            }
            const savedUpVoucherAmount = savedUpVoucher.amount > newResidueVoucher.amount * 0.8
                ? newResidueVoucher.amount * 0.8
                : savedUpVoucher.amount;
            if (!savedUpVoucherAmount) {
                break;
            }
            newVouchers = newVouchers.concat({
                ...savedUpVoucher,
                billingVoucherId: uuid(),
                currency: newResidueVoucher.currency,
                date: newResidueVoucher.date,
                caseVoucherId: newResidueVoucher.caseVoucherId,
                amount: savedUpVoucherAmount,
                details: {
                    ...savedUpVoucher.details,
                    savedUpVatAmount: savedUpVoucherAmount * 0.25,
                    despositVoucherTypeId: newResidueVoucher.details?.despositVoucherTypeId,
                },
            });
            newResidueVouchers[x].amount -= savedUpVoucherAmount * 1.25;
            savedUpVouchers[i].amount -= savedUpVoucherAmount;
        }
        // Remove savedUpVouchers without amount
        savedUpVouchers = savedUpVouchers.filter((savedUpVoucher) => !!savedUpVoucher.amount);
    }
    return [newResidueVouchers, savedUpVouchers, newVouchers];
};
export const handlePreventNegativeCreditorBalance = (billingModel, amount, newCollectionCommissionVoucher, toVoucherTypeIds, share) => {
    if (billingModel.model.preventNegativeCreditorBalance) {
        // Vat amount so that the creditor balance will be 0
        const saveUpAmount = amount * 0.2;
        const savedUpVoucher = {
            ...newCollectionCommissionVoucher,
            shareId: share.shareId,
            billingVoucherId: undefined,
            amount: saveUpAmount,
            voucherTypeId: toVoucherTypeIds[0],
        };
        return [
            toVoucherTypeIds.map((toVoucherTypeId) => ({
                ...newCollectionCommissionVoucher,
                billingVoucherId: uuid(),
                shareId: share.shareId,
                amount: amount - saveUpAmount,
                voucherTypeId: toVoucherTypeId,
                details: {
                    savedUpVoucher,
                    savedUpVatAmount: saveUpAmount,
                },
            })),
            savedUpVoucher,
        ];
    }
    else {
        return [
            toVoucherTypeIds.map((toVoucherTypeId) => ({
                ...newCollectionCommissionVoucher,
                shareId: share.shareId,
                billingVoucherId: uuid(),
                amount,
                voucherTypeId: toVoucherTypeId,
            })),
            null,
        ];
    }
};
export const getOverheadVouchers = (baseVoucher, caseVoucherWithDistribution, _case, billingModel) => {
    let newOverheadVouchers = [];
    const from = billingModel.model.from;
    const caseVouchersOfType = from
        ? caseVoucherWithDistribution.filter((v) => matchesVoucherType(from, v) ||
            v.voucherTypeId === VoucherTypeIds.REFUND)
        : caseVoucherWithDistribution;
    for (let i = 0; i < caseVouchersOfType.length; i++) {
        const caseVoucher = caseVouchersOfType[i];
        if (caseVoucher.distributionAmount < caseVoucher.amount &&
            caseBillingIsActive(caseVoucher.date, _case) &&
            (caseVoucher.source !== CaseVoucherSource.Creditor ||
                billingModel.model.isAccountsReceivableBillingModel)) {
            newOverheadVouchers = newOverheadVouchers.concat({
                ...baseVoucher,
                billingVoucherId: uuid(),
                voucherTypeId: VoucherTypeIds.OVERPAYMENT,
                date: caseVoucher.date,
                currency: caseVoucher.currency,
                amount: Math.round(caseVoucher.amount - caseVoucher.distributionAmount),
                depositCaseVoucherId: caseVoucher.caseVoucherId,
                depositCaseVoucher: mapDepositCaseVoucher(caseVoucher),
                billingModelId: billingModel.billingModelId,
            });
        }
    }
    return newOverheadVouchers;
};
const mapDepositCaseVoucher = (caseVoucher) => ({
    caseVoucherId: caseVoucher.caseVoucherId,
    date: caseVoucher.date,
    id: caseVoucher.id,
    referenceId: caseVoucher.referenceId,
    text: caseVoucher.text,
    source: caseVoucher.source,
    paymentType: caseVoucher.paymentType,
    paymentMethodId: caseVoucher.paymentMethodId,
    state: caseVoucher.state,
});
export const forceCollectionCommissionBilling = async (_case, billingModel, newBillingVouchers, caseVouchers, baseBillingVoucher, documents, economyPolicy, fixedDistributions, conditions) => {
    let _newBillingVouchers = [];
    const shareWithForceCollectionCommissionBilling = billingModel.model.share.filter((share) => !!share.forceCollectionCommissionBilling);
    if (shareWithForceCollectionCommissionBilling.length > 0) {
        for (let k = 0; k < shareWithForceCollectionCommissionBilling.length; k++) {
            const forceShare = shareWithForceCollectionCommissionBilling[k];
            if (forceShare.forceCollectionCommissionBilling?.withCaseTag &&
                forceShare.forceCollectionCommissionBilling.voucherTypeId) {
                const tag = _case.tags.find((t) => t.tag === forceShare.forceCollectionCommissionBilling?.withCaseTag);
                const date = tag?.from;
                if (tag && date) {
                    const caseVouchersWithInterestToDate = await helpers.interest.calculate(caseVouchers, documents, economyPolicy, fixedDistributions, date, conditions);
                    if (!caseVouchersWithInterestToDate?.length) {
                        return newBillingVouchers;
                    }
                    const alreadyBilledMap = newBillingVouchers.reduce((acc, billingVoucher) => {
                        if (billingVoucher.caseVoucherId) {
                            if (!acc[billingVoucher.caseVoucherId]) {
                                acc[billingVoucher.caseVoucherId] = 0;
                            }
                            acc[billingVoucher.caseVoucherId] += billingVoucher.amount;
                        }
                        return acc;
                    }, {});
                    // Force billing
                    for (let i = 0; i < caseVouchersWithInterestToDate.length; i++) {
                        const caseVoucher = caseVouchersWithInterestToDate[i];
                        // Share matches?
                        const shares = getCollectionCommissionMatches(billingModel, _case, caseVoucher.date, caseVoucher);
                        for (let f = 0; f < shares.length; f++) {
                            const share = shares[f];
                            if (share.shareId !== forceShare.shareId) {
                                continue;
                            }
                            const tag = _case.tags.find((t) => t.tag === share.forceCollectionCommissionBilling?.withCaseTag);
                            const date = tag?.from;
                            if (share.forceCollectionCommissionBilling && date) {
                                const totalAmount = Math.round(-caseVoucher.amount * (share.feeFraction || 1));
                                const alreadyBilled = alreadyBilledMap[caseVoucher.caseVoucherId] || 0;
                                // Force bill?
                                if (totalAmount - alreadyBilled > 0) {
                                    _newBillingVouchers = _newBillingVouchers.concat({
                                        ...baseBillingVoucher,
                                        billingModelId: billingModel.billingModelId,
                                        currency: caseVoucher.currency,
                                        date: date,
                                        caseVoucherId: caseVoucher.caseVoucherId,
                                        caseVoucher: {
                                            caseVoucherId: caseVoucher.caseVoucherId,
                                            voucherTypeId: caseVoucher.voucherTypeId,
                                            date: caseVoucher.date,
                                            id: caseVoucher.id,
                                            referenceId: caseVoucher.referenceId,
                                            source: caseVoucher.source,
                                            amount: caseVoucher.amount,
                                        },
                                        shareId: share.shareId,
                                        billingVoucherId: uuid(),
                                        amount: totalAmount - alreadyBilled,
                                        voucherTypeId: share.forceCollectionCommissionBilling.voucherTypeId,
                                    });
                                }
                            }
                        }
                    }
                }
            }
        }
    }
    return _newBillingVouchers;
};
//# sourceMappingURL=helpers.js.map